import type { TooltipProps } from '../../../components/Tooltip';
import { TooltipWithContactUs } from '../../../components/TooltipWithContactUs';
import { INSIGHTS_FEATURE_KEY } from '../../../domain/gainAccessView';
import { INSIGHTS_LIMIT_INCREASE } from '../../../domain/user';
import { useUserStatus } from '../../../hooks/useUserStatus';
import { translate } from '../../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

export type InsightsQuotaTooltipLocation =
  | 'preferences'
  | 'regenerate'
  | 'competitors'
  | 'get more insights'
  | 'top counter';

export const InsightsQuotaTooltip = ({
  location,
  children,
  ...rest
}: {
  location: InsightsQuotaTooltipLocation;
  children: React.ReactNode;
} & Omit<TooltipProps, 'content'>) => {
  const { isTrial, isAfterTrial } = useUserStatus();

  const dispatchTrackEvent = useDispatchTrackEventWithLocation();

  const handleClick = () => {
    dispatchTrackEvent('hooks', 'contact us', `limit reached/${location || ''}`);
  };

  const getTooltipKey = () => {
    if (isTrial) {
      return `insights.limit_reached.tooltip.trial`;
    }
    if (isAfterTrial) {
      return `insights.limit_reached.tooltip.after-trial`;
    }
    return `insights.limit_reached.tooltip.paid`;
  };

  const getAccessRequestedKey = () => {
    if (isTrial) {
      return `insights.limit_reached.access_requested.tooltip.trial`;
    }
    if (isAfterTrial) {
      return `insights.limit_reached.access_requested.tooltip.after-trial`;
    }
    return `insights.limit_reached.access_requested.tooltip.paid`;
  };

  return (
    <TooltipWithContactUs
      {...rest}
      featureKey={INSIGHTS_FEATURE_KEY}
      settingKey={INSIGHTS_LIMIT_INCREASE}
      onContactUsClick={handleClick}
      text={translate(getTooltipKey())}
      accessRequestedText={translate(getAccessRequestedKey())}
      debounceOnLeave={300}
    >
      {children}
    </TooltipWithContactUs>
  );
};
